import React, { useState, useEffect } from "react";
import "../styles/UserDashboard.css";
import {
  HiLightBulb,
  HiUser,
  HiChatBubbleLeftRight,
  HiBell,
  HiArrowsRightLeft,
  HiArrowDown,
  HiClock,
  HiShoppingCart,
  HiChevronRight,
  HiXMark,
  HiPause,
  HiLockClosed,
  HiNoSymbol,
  HiClipboard,
  HiPlus,
  HiGlobeAlt,
  HiShieldCheck,
} from "react-icons/hi2";
import { BiGridAlt, BiCog, BiStar } from "react-icons/bi";
import {
  MdCreditCard,
  MdTrendingUp,
  MdTrendingDown,
  MdAccountBalance,
} from "react-icons/md";
import { BsBank2 } from "react-icons/bs";
import { RiStockLine, RiShieldCheckLine } from "react-icons/ri";
import { FaPlane, FaChartLine } from "react-icons/fa";

// Import sub-pages
import BankPage from "../pages/BankPage";
import InvestSavePage from "../pages/InvestSavePage";
import InsurePage from "../pages/InsurePage";
import FinancePage from "../pages/FinancePage";
import TravelPage from "../pages/TravelPage";
import TransactPage from "../pages/TransactPage";
import ManagePage from "../pages/ManagePage";
import ForYouPage from "../pages/ForYouPage";
import MorePage from "../pages/MorePage";
import ProfileModal from "../modals/ProfileModal";
import NotificationModal from "../modals/NotificationModal";
import ErrorModal from "../modals/ErrorModal";
import sessionManager from "../utils/sessionManager";

const UserDashboard = () => {
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [cards, setCards] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [activeBottomTab, setActiveBottomTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pockets, setPockets] = useState([]);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(120); // 2 minutes

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Initialize session manager
  useEffect(() => {
    const handleWarning = () => {
      setShowSessionWarning(true);

      // Start countdown
      let countdown = 120; // 2 minutes
      const countdownInterval = setInterval(() => {
        countdown--;
        setWarningCountdown(countdown);

        if (countdown <= 0) {
          clearInterval(countdownInterval);
        }
      }, 1000);
    };

    const handleLogout = () => {
      sessionManager.logout();
    };

    sessionManager.init(handleWarning, handleLogout);

    // Cleanup on unmount
    return () => {
      sessionManager.cleanup();
    };
  }, []);

  const handleExtendSession = () => {
    setShowSessionWarning(false);
    setWarningCountdown(120);
    sessionManager.extendSession();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");

      console.log("=== DASHBOARD FETCH START ===");
      console.log("Token exists:", !!token);

      if (!token) {
        console.log("❌ No auth token found - stopping fetch");
        setLoading(false);
        return;
      }

      const response = await fetch(
        "https://shop.faithhomecares.co.uk/api/v1/users/dashboard",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      console.log("📦 Raw API Response:", JSON.stringify(data, null, 2));

      if (data.success) {
        const userData = data.data.user || data.data;

        console.log("👤 Mapped user data:", userData);

        // Split fullName into firstName and lastName
        const nameParts = userData.fullName
          ? userData.fullName.split(" ")
          : ["", ""];
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || ""; // Handle multiple last names

        // Set user data with proper mapping
        const mappedUser = {
          _id: userData._id,
          fullName: userData.fullName,
          firstName: firstName,
          lastName: lastName,
          email: userData.email,
          phoneNumber: userData.phoneNumber,
          accountNumber: userData.accountNumber,
          balance: userData.balance || 0,
          availableBalance: userData.balance || 0,
          primaryCurrency: userData.currency || "ZAR",
          profilePicture: userData.profilePicture,
          address: userData.address,
          createdAt: userData.createdAt,
        };

        console.log("✅ Setting user state with:", mappedUser);
        setUser(mappedUser);

        // Map card details
        if (userData.cardDetails) {
          const mappedCards = [
            {
              id: userData._id,
              cardNumber: userData.cardDetails.cardNumber,
              cardType: "Platinum Card",
              expiryDate: userData.cardDetails.expiryDate,
              cvv: userData.cardDetails.cvv,
              cardHolderName: userData.cardDetails.cardHolderName,
              design: "zebra",
              status: "active",
            },
          ];
          console.log("✅ Setting cards state with:", mappedCards);
          setCards(mappedCards);
        } else {
          console.log("⚠️ No cardDetails found in userData");
        }

        // Set other data
        setAccounts(data.data.accounts || []);
        setTransactions(
          data.data.recentTransactions || data.data.transactions || [],
        ); // Support both field names
        setPockets(data.data.pockets || []);

        console.log("=== DASHBOARD FETCH SUCCESS ===");
      } else {
        console.log("❌ API returned unsuccessful response:", data);
      }
    } catch (error) {
      console.error("💥 ERROR fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setShowError(true);
  };

  const handleIdeasClick = () => {
    showErrorMessage(
      "Ideas & Insights feature is currently unavailable. Our team is working on bringing you personalized financial insights soon.",
    );
  };

  const handleProfileClick = () => {
    setShowProfile(true);
  };

  const handleMessagesClick = () => {
    showErrorMessage(
      "Messaging feature is currently unavailable. For urgent matters, please contact support.",
    );
  };

  const handleNotificationsClick = () => {
    setShowNotifications(true);
  };

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowCardDetails(true);
  };

  const handleCardAction = async (action, card) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/users/cards/${card.id}/${action}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        alert(`Card ${action} successful`);
        if (action === "pause") {
          setShowCardDetails(false);
        }
        fetchDashboardData();
      } else {
        showErrorMessage(data.message || `Failed to ${action} card`);
      }
    } catch (error) {
      showErrorMessage(`Unable to ${action} card. Please try again.`);
    }
  };

  const togglePocket = async (id) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(
        `https://shop.faithhomecares.co.uk/api/v1/users/pockets/${id}/toggle`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setPockets(
          pockets.map((pocket) =>
            pocket.id === id ? { ...pocket, enabled: !pocket.enabled } : pocket,
          ),
        );
      } else {
        showErrorMessage(data.message || "Failed to toggle pocket");
      }
    } catch (error) {
      showErrorMessage("Unable to toggle pocket. Please try again.");
    }
  };

  const handleAddPocket = () => {
    showErrorMessage(
      "Add Pocket feature is currently unavailable. Please contact support to add a new currency pocket.",
    );
  };

  const handleQuickAction = (action) => {
    const actionName = action.charAt(0).toUpperCase() + action.slice(1);
    showErrorMessage(
      `${actionName} feature is temporarily blocked. Tax payment Required.`,
    );
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text);
      alert("Copied to clipboard!");
    }
  };

  const formatCurrency = (amount, currency = "ZAR") => {
    if (!amount && amount !== 0) return "0.00";
    const symbols = { ZAR: "R", USD: "$", GBP: "£", EUR: "€" };
    return `${symbols[currency] || currency}${Number(amount).toLocaleString(
      "en-US",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getCurrencyFlag = (currency) => {
    const flags = {
      ZAR: "🇿🇦",
      USD: "🇺🇸",
      GBP: "🇬🇧",
      EUR: "🇪🇺",
    };
    return flags[currency] || "💱";
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case "bank":
        return <BankPage user={user} accounts={accounts} />;
      case "invest":
        return <InvestSavePage user={user} />;
      case "insure":
        return <InsurePage user={user} />;
      case "finance":
        return <FinancePage user={user} />;
      case "travel":
        return <TravelPage user={user} />;
      default:
        return renderOverviewContent();
    }
  };

  const renderBottomTabContent = () => {
    // If a bottom tab OTHER than overview is selected, show that tab's content
    if (activeBottomTab !== "overview") {
      switch (activeBottomTab) {
        case "transact":
          return <TransactPage user={user} onError={showErrorMessage} />;
        case "manage":
          return <ManagePage user={user} />;
        case "foryou":
          return <ForYouPage user={user} />;
        case "more":
          return <MorePage user={user} />;
        default:
          return null;
      }
    }

    // If overview is selected, show content based on the top tab
    return renderContent();
  };

  const renderOverviewContent = () => (
    <>
      <section className="account-section">
        <div className="account-header">
          <div>
            <h3 className="account-title">
              {user?.firstName} {user?.lastName}
            </h3>
            <p className="account-number">Private Bank Account</p>
            <p className="account-number">{user?.accountNumber}</p>
          </div>
          <button
            className="icon-btn"
            onClick={() => showErrorMessage("Account options coming soon")}
          >
            ⋮
          </button>
        </div>

        <div className="balance-display">
          <p className="balance-label">
            <span className="flag-icon">
              {getCurrencyFlag(user?.primaryCurrency)}
            </span>{" "}
            {user?.primaryCurrency}
          </p>
          <h1 className="balance-amount">
            {formatCurrency(user?.balance, user?.primaryCurrency)}
          </h1>
          <p className="pocket-available">
            Avail.{" "}
            {formatCurrency(user?.availableBalance, user?.primaryCurrency)}
          </p>
        </div>
      </section>

      {pockets && pockets.length > 0 && (
        <section className="pockets-section">
          <div className="pockets-grid">
            {pockets.map((pocket) => (
              <div key={pocket.id || pocket._id} className="pocket-card">
                <div className="pocket-info">
                  <div className="pocket-header">
                    <span className="flag-icon">
                      {getCurrencyFlag(pocket.currency)}
                    </span>
                    <h4 className="pocket-title">{pocket.currency} Pocket</h4>
                  </div>
                  <h2 className="pocket-balance">
                    {formatCurrency(pocket.balance, pocket.currency)}
                  </h2>
                  <p className="pocket-available">
                    Avail. {formatCurrency(pocket.available, pocket.currency)}
                  </p>
                </div>
                <button
                  className={`pocket-toggle ${pocket.enabled ? "on" : ""}`}
                  onClick={() => togglePocket(pocket.id || pocket._id)}
                >
                  {pocket.enabled ? "On" : "Off"}
                </button>
              </div>
            ))}

            <div className="pocket-card add-pocket">
              <button className="add-pocket-btn" onClick={handleAddPocket}>
                <span className="plus-icon">
                  <HiPlus />
                </span>
                <p>Add pocket</p>
              </button>
            </div>
          </div>
        </section>
      )}

      <section className="card-carousel">
        <div className="carousel-header">
          <h2 className="carousel-title">Cards</h2>
        </div>

        {cards && cards.length > 0 ? (
          <>
            <div className="cards-wrapper">
              {cards.map((card, index) => (
                <div
                  key={card.id || index}
                  className={`card-item ${card.design === "zebra" ? "zebra-card" : ""}`}
                  onClick={() => handleCardClick(card)}
                  style={{
                    backgroundImage: card.backgroundImage
                      ? `url(${card.backgroundImage})`
                      : "none",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  {card.backgroundImage && <div className="card-overlay"></div>}

                  <div className="card-brand">
                    <div className="investec-logo">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                        <path
                          d="M12 6v12M6 12h12"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                      <span>Investec</span>
                    </div>
                  </div>

                  {card.design === "zebra" && !card.backgroundImage && (
                    <div className="zebra-pattern"></div>
                  )}

                  <div className="card-details">
                    <div className="card-number">
                      {card.cardNumber || "•••• •••• •••• ••••"}
                    </div>
                    <div className="card-info-row">
                      <span>{card.expiryDate || "MM/YY"}</span>
                      <span className="visa-logo">VISA</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="card-info-badge">
              <div className="badge-content">
                <p className="card-type-name">
                  {cards[0]?.cardType || "Platinum Card"}
                </p>
                <div className="card-meta">
                  <span className="card-physical-badge">Physical</span>
                  <span className="card-multicurrency-badge">
                    <HiGlobeAlt className="globe-icon" />
                    Multicurrency
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="empty-state">
            <MdCreditCard className="empty-icon" />
            <p>No cards available</p>
          </div>
        )}
      </section>

      <section className="quick-actions">
        <button className="action-btn" onClick={() => handleQuickAction("pay")}>
          <span className="action-icon">
            <HiArrowsRightLeft />
          </span>
          <span>Pay</span>
        </button>
        <button
          className="action-btn"
          onClick={() => handleQuickAction("transfer")}
        >
          <span className="action-icon">
            <HiArrowDown />
          </span>
          <span>Transfer</span>
        </button>
        <button
          className="action-btn"
          onClick={() => handleQuickAction("schedule")}
        >
          <span className="action-icon">
            <HiClock />
          </span>
          <span>Schedule</span>
        </button>
        <button className="action-btn" onClick={() => handleQuickAction("buy")}>
          <span className="action-icon">
            <HiShoppingCart />
          </span>
          <span>Buy</span>
        </button>
      </section>

      <section className="transactions-section">
        <div className="section-header">
          <h2>Recent Transactions</h2>
          <button
            className="view-all-btn"
            onClick={() => showErrorMessage("Transaction history coming soon")}
          >
            View more
          </button>
        </div>

        <div className="transactions-list">
          {transactions && transactions.length > 0 ? (
            transactions.slice(0, 5).map((transaction) => (
              <div
                key={transaction._id || transaction.id}
                className="transaction-item"
              >
                <div className="transaction-icon">
                  {transaction.type === "credit" ? (
                    <MdTrendingUp />
                  ) : (
                    <MdTrendingDown />
                  )}
                </div>
                <div className="transaction-details">
                  <h4>{transaction.description || "Transaction"}</h4>
                  <p>{formatDate(transaction.createdAt || transaction.date)}</p>
                </div>
                <div className={`transaction-amount ${transaction.type}`}>
                  {transaction.type === "credit" ? "+" : "-"}
                  {formatCurrency(transaction.amount, transaction.currency)}
                </div>
                <button className="arrow-btn">
                  <HiChevronRight />
                </button>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <MdAccountBalance className="empty-icon" />
              <p>No recent transactions</p>
            </div>
          )}
        </div>
      </section>
    </>
  );

  return (
    <div className="user-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="user-greeting">
            <h2 className="greeting-text">
              <span className="greeting-time">{getGreeting()},</span>{" "}
              <span className="greeting-name">{user?.fullName}</span>
            </h2>
          </div>
          <div className="header-icons">
            <button
              className="icon-btn"
              onClick={handleIdeasClick}
              title="Ideas & Insights"
            >
              <HiLightBulb />
            </button>
            <button
              className="icon-btn"
              onClick={handleProfileClick}
              title="Profile"
            >
              <HiUser />
            </button>
            <button
              className="icon-btn"
              onClick={handleMessagesClick}
              title="Messages"
            >
              <HiChatBubbleLeftRight />
            </button>
            <button
              className="icon-btn"
              onClick={handleNotificationsClick}
              title="Notifications"
            >
              <HiBell />
            </button>
          </div>
        </div>
      </header>

      <nav className="tab-navigation">
        <div className="tab-list">
          <button
            className={`tab-btn ${activeTab === "all" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("all");
              setActiveBottomTab("overview");
            }}
          >
            All
          </button>
          <button
            className={`tab-btn ${activeTab === "bank" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("bank");
              setActiveBottomTab("overview");
            }}
          >
            <BsBank2 className="tab-icon-inline" />
            Bank
          </button>
          <button
            className={`tab-btn ${activeTab === "invest" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("invest");
              setActiveBottomTab("overview");
            }}
          >
            <RiStockLine className="tab-icon-inline" />
            Invest & Save
          </button>
          <button
            className={`tab-btn ${activeTab === "insure" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("insure");
              setActiveBottomTab("overview");
            }}
          >
            <RiShieldCheckLine className="tab-icon-inline" />
            Insure
          </button>
          <button
            className={`tab-btn ${activeTab === "finance" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("finance");
              setActiveBottomTab("overview");
            }}
          >
            <FaChartLine className="tab-icon-inline" />
            Finance
          </button>
          <button
            className={`tab-btn ${activeTab === "travel" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("travel");
              setActiveBottomTab("overview");
            }}
          >
            <FaPlane className="tab-icon-inline" />
            Travel
          </button>
        </div>
      </nav>

      <main className="dashboard-content">{renderBottomTabContent()}</main>

      <nav className="bottom-nav">
        <button
          className={`bottom-nav-item ${activeBottomTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveBottomTab("overview")}
        >
          <span className="nav-icon">
            <BiGridAlt />
          </span>
          <span>Overview</span>
        </button>
        <button
          className={`bottom-nav-item ${activeBottomTab === "transact" ? "active" : ""}`}
          onClick={() => setActiveBottomTab("transact")}
        >
          <span className="nav-icon">
            <MdCreditCard />
          </span>
          <span>Transact</span>
        </button>
        <button
          className={`bottom-nav-item ${activeBottomTab === "manage" ? "active" : ""}`}
          onClick={() => setActiveBottomTab("manage")}
        >
          <span className="nav-icon">
            <BiCog />
          </span>
          <span>Manage</span>
        </button>
        <button
          className={`bottom-nav-item ${activeBottomTab === "foryou" ? "active" : ""}`}
          onClick={() => setActiveBottomTab("foryou")}
        >
          <span className="nav-icon">
            <BiStar />
          </span>
          <span>For you</span>
        </button>
        <button
          className={`bottom-nav-item ${activeBottomTab === "more" ? "active" : ""}`}
          onClick={() => setActiveBottomTab("more")}
        >
          <span className="nav-icon">•••</span>
          <span>More</span>
        </button>
      </nav>

      {showProfile && (
        <ProfileModal user={user} onClose={() => setShowProfile(false)} />
      )}

      {showNotifications && (
        <NotificationModal onClose={() => setShowNotifications(false)} />
      )}

      {showError && (
        <ErrorModal
          message={errorMessage}
          onClose={() => setShowError(false)}
        />
      )}

      {showCardDetails && selectedCard && (
        <div
          className="modal-overlay"
          onClick={() => setShowCardDetails(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="close-btn"
              onClick={() => setShowCardDetails(false)}
            >
              <HiXMark />
            </button>

            <h2>Card Details</h2>

            <div
              className={`card-item ${selectedCard.design === "zebra" ? "zebra-card" : ""}`}
              style={{
                marginBottom: "1.5rem",
                cursor: "default",
                backgroundImage: selectedCard.backgroundImage
                  ? `url(${selectedCard.backgroundImage})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {selectedCard.backgroundImage && (
                <div className="card-overlay"></div>
              )}

              <div className="card-brand">
                <div className="investec-logo">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M12 6v12M6 12h12"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                  </svg>
                  <span>Investec</span>
                </div>
              </div>

              {selectedCard.design === "zebra" &&
                !selectedCard.backgroundImage && (
                  <div className="zebra-pattern"></div>
                )}

              <div className="card-details">
                <div className="card-number">
                  •••• •••• •••• {selectedCard.cardNumber?.slice(-4) || "••••"}
                </div>
                <div className="card-info-row">
                  <span>{selectedCard.expiryDate || "MM/YY"}</span>
                  <span className="visa-logo">VISA</span>
                </div>
              </div>
            </div>

            <div className="card-actions-grid">
              <button
                className="card-action"
                onClick={() => handleCardAction("pause", selectedCard)}
              >
                <span className="action-icon">
                  <HiPause />
                </span>
                <span>Pause</span>
              </button>
              <button
                className="card-action"
                onClick={() => handleCardAction("pin", selectedCard)}
              >
                <span className="action-icon">
                  <HiLockClosed />
                </span>
                <span>Card PIN</span>
              </button>
              <button
                className="card-action"
                onClick={() => handleCardAction("cancel", selectedCard)}
              >
                <span className="action-icon">
                  <HiNoSymbol />
                </span>
                <span>Cancel and replace</span>
              </button>
            </div>

            <div className="info-section">
              <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem" }}>
                Card Information
              </h3>

              <div className="info-item">
                <span className="label">Account number</span>
                <div className="value-row">
                  <span>{user?.accountNumber}</span>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(user?.accountNumber)}
                  >
                    <HiClipboard />
                  </button>
                </div>
              </div>

              <div className="info-item">
                <span className="label">Card number</span>
                <div className="value-row">
                  <span>{selectedCard.cardNumber || "N/A"}</span>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(selectedCard.cardNumber)}
                  >
                    <HiClipboard />
                  </button>
                </div>
              </div>

              <div className="info-item">
                <span className="label">Expiry date</span>
                <div className="value-row">
                  <span>{selectedCard.expiryDate || "N/A"}</span>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(selectedCard.expiryDate)}
                  >
                    <HiClipboard />
                  </button>
                </div>
              </div>

              <div className="info-item">
                <span className="label">CVV</span>
                <div className="value-row">
                  <span>{selectedCard.cvv || "N/A"}</span>
                  <button
                    className="copy-btn"
                    onClick={() => copyToClipboard(selectedCard.cvv)}
                  >
                    <HiClipboard />
                  </button>
                </div>
              </div>
            </div>

            <p className="security-notice">
              <HiShieldCheck className="security-icon" />
              This is sensitive information. Please store this information as
              safe as you would your physical Investec card.
            </p>
          </div>
        </div>
      )}
      {showSessionWarning && (
        <div className="modal-overlay">
          <div
            className="modal-content session-warning-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="warning-icon">
              <HiClock />
            </div>

            <h2>Session Expiring Soon</h2>

            <p className="warning-text">
              Your session will expire in{" "}
              <strong>
                {Math.floor(warningCountdown / 60)}:
                {String(warningCountdown % 60).padStart(2, "0")}
              </strong>{" "}
              due to inactivity.
            </p>

            <p className="warning-subtext">
              Click "Stay Logged In" to continue your session, or you'll be
              automatically logged out.
            </p>

            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => sessionManager.logout()}
              >
                Logout Now
              </button>
              <button className="btn-primary" onClick={handleExtendSession}>
                Stay Logged In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;
